<template>
	<div class="dashboard-customizado">
		<!-- Seletor exibido apenas enquanto o usuario nao escolheu um relatorio -->
		<div
			v-if="mostrarSeletor"
			class="dashboard-customizado__barra"
		>
			<!-- Mesmo container do menu do sistema (Menu.vue) para manter o alinhamento -->
			<div class="dashboard-customizado__barra-conteudo">
				<label
					class="dashboard-customizado__label"
					for="dashboard-customizado-select"
				>
					{{ $t('selectReport') }}:
				</label>
				<select
					id="dashboard-customizado-select"
					class="dashboard-customizado__select"
					:value="nomeRelatorio"
					@change="selecionarRelatorio($event.target.value)"
				>
					<option
						v-for="relatorio in relatoriosDisponiveis"
						:key="relatorio.nome"
						:value="relatorio.nome"
					>
						{{ relatorio.titulo }}
					</option>
				</select>
				<button
					type="button"
					class="dashboard-customizado__icone"
					:title="$t('recarregarRelatorio')"
					:disabled="loading"
					@click="carregarRelatorio(true)"
				>
					<i class="fas fa-sync-alt" />
				</button>
			</div>
		</div>

		<div class="dashboard-customizado__conteudo">
			<!--
				Reabre o seletor depois que ele foi oculto pela escolha do relatorio.
				Fica invisivel ate o mouse entrar na area do relatorio.
			-->
			<div
				v-if="possuiMaisDeUmRelatorio && !mostrarSeletor"
				class="dashboard-customizado__reabrir-area"
			>
				<button
					type="button"
					class="dashboard-customizado__reabrir"
					:title="$t('selectReport')"
					@click="seletorOculto = false"
				>
					<i class="fas fa-exchange-alt" />
					<span class="dashboard-customizado__reabrir-texto">
						{{ tituloRelatorioSelecionado }}
					</span>
				</button>
			</div>

			<div
				v-if="loading"
				class="dashboard-customizado__estado"
			>
				<v-progress-circular
					indeterminate
					color="primary"
					size="40"
				/>
			</div>
			<div
				v-else-if="semRelatorio"
				class="dashboard-customizado__estado"
			>
				<i class="fas fa-file-alt dashboard-customizado__estado-icone" />
				<p>{{ $t('semRelatorioCadastrado') }}</p>
			</div>
			<div
				v-else-if="erro"
				class="dashboard-customizado__estado"
			>
				<p>{{ $t('erroAoCarregarDashboard') }}</p>
				<button
					type="button"
					class="dashboard-customizado__tentar-novamente"
					@click="carregarRelatorio(true)"
				>
					{{ $t('tentarNovamente') }}
				</button>
			</div>
			<iframe
				v-else
				class="dashboard-customizado__frame"
				:srcdoc="html"
				:title="tituloRelatorioSelecionado"
			/>
		</div>
	</div>
</template>

<script>
import { buscarRelatoriosCustomizadosDoClienteLogado } from '../../constants/Mappings/RelatoriosCustomizadosPorCliente'
import { getRelatorioCustomizado } from '../../services/requests/DashBoardRequests'

export default {
	data() {
		return {
			html: '',
			loading: true,
			erro: false,
			// Cliente sem relatorio mapeado ou relatorio inexistente no backend
			semRelatorio: false,
			// Relatorios que o cliente logado pode visualizar
			relatoriosDoCliente: buscarRelatoriosCustomizadosDoClienteLogado(),
			// Cache do HTML por relatorio para que trocar de relatorio nao refaca a requisicao
			htmlPorRelatorio: {},
			// Depois que o usuario escolhe um relatorio o seletor sai da tela,
			// liberando espaco para o relatorio. O botao flutuante o traz de volta.
			seletorOculto: false,
		}
	},
	computed: {
		/**
		 * Relatorio exibido. Vem da rota; se a rota nao trouxer o parametro usa o
		 * primeiro relatorio do cliente. Vazio quando o cliente nao possui relatorio.
		 */
		nomeRelatorio() {
			return this.$route.params.nome || this.relatoriosDoCliente[0]?.nome || ''
		},
		/**
		 * Opcoes do seletor. Garante que um relatorio acessado direto pela URL
		 * (fora do mapeamento do cliente) tambem apareca como opcao.
		 */
		relatoriosDisponiveis() {
			const relatorios = [...this.relatoriosDoCliente]

			if (
				this.nomeRelatorio &&
				!relatorios.some((relatorio) => relatorio.nome === this.nomeRelatorio)
			) {
				relatorios.unshift({ nome: this.nomeRelatorio, titulo: this.nomeRelatorio })
			}

			return relatorios
		},
		possuiMaisDeUmRelatorio() {
			return this.relatoriosDisponiveis.length > 1
		},
		mostrarSeletor() {
			return this.possuiMaisDeUmRelatorio && !this.seletorOculto
		},
		tituloRelatorioSelecionado() {
			const relatorio = this.relatoriosDisponiveis.find(({ nome }) => nome === this.nomeRelatorio)
			return relatorio?.titulo || 'Dashboard Customizado'
		},
	},
	watch: {
		// Recarrega ao trocar de relatorio sem sair da rota.
		nomeRelatorio() {
			this.carregarRelatorio()
		},
	},
	created() {
		this.carregarRelatorio()
	},
	methods: {
		/**
		 * Troca o relatorio exibido mantendo a URL sincronizada (link compartilhavel)
		 * e oculta o seletor.
		 */
		selecionarRelatorio(nome) {
			this.seletorOculto = true

			if (nome === this.nomeRelatorio) return

			this.$router.replace({
				name: 'dashboardCustomizado',
				params: { nome },
			})
		},
		/**
		 * @param {boolean} forcarAtualizacao Ignora o cache e busca o HTML novamente
		 */
		carregarRelatorio(forcarAtualizacao = false) {
			const nome = this.nomeRelatorio

			// Cliente sem nenhum relatorio liberado: nem chega a requisitar
			if (!nome) {
				this.html = ''
				this.erro = false
				this.semRelatorio = true
				this.loading = false
				return
			}

			if (!forcarAtualizacao && this.htmlPorRelatorio[nome] !== undefined) {
				this.html = this.htmlPorRelatorio[nome]
				this.erro = false
				this.semRelatorio = false
				this.loading = false
				return
			}

			this.loading = true
			this.erro = false
			this.semRelatorio = false
			getRelatorioCustomizado(nome)
				.then((response) => {
					// O backend responde 200 com text/plain ("Relatório não encontrado.")
					// quando o nome nao esta na whitelist dele, por isso a checagem.
					const contentType = (response?.headers?.['content-type'] || '').toLowerCase()
					const html = response?.data || ''
					const relatorioIndisponivel = !html || (contentType && !contentType.includes('text/html'))

					if (!relatorioIndisponivel) {
						this.htmlPorRelatorio = { ...this.htmlPorRelatorio, [nome]: html }
					}

					// Ignora respostas de um relatorio que nao esta mais selecionado
					if (nome !== this.nomeRelatorio) return

					if (relatorioIndisponivel) {
						this.html = ''
						this.semRelatorio = true
					} else {
						this.html = html
					}

					this.loading = false
				})
				.catch((error) => {
					if (nome !== this.nomeRelatorio) return

					// Relatorio inexistente nao e falha de carregamento
					if (error?.response?.status === 404) {
						this.semRelatorio = true
					} else {
						this.erro = true
					}

					this.loading = false
				})
		},
	},
}
</script>

<style scoped>
.dashboard-customizado {
	/* Ocupa a area de conteudo entre o header (4rem) e o footer (2.5rem) do Main.vue */
	height: calc(100vh - 4rem - 2.5rem);
	width: 100%;
	display: flex;
	flex-direction: column;
}

.dashboard-customizado__barra {
	border-bottom: 1px solid #e0e0e0;
	background-color: #fff;
}

/* Mesmas medidas do .container__menu (Menu.vue) para alinhar com o menu do sistema */
.dashboard-customizado__barra-conteudo {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
	width: 100%;
	max-width: 1200px;
	margin: 0 auto;
	padding: 0.75rem 2rem;
}

@media screen and (min-width: 1200px) {
	.dashboard-customizado__barra-conteudo {
		padding-left: 0;
		padding-right: 0;
	}
}

.dashboard-customizado__label {
	font-size: 0.9rem;
	font-weight: 600;
	color: var(--color-navy-blue, #2c3e50);
}

/* Mesma aparencia do seletor de relatorios do Power BI (PowerBI.vue) */
.dashboard-customizado__select {
	flex: 1;
	max-width: 400px;
	padding: 0.5rem 0.75rem;
	font-size: 0.9rem;
	border: 2px solid #e0e0e0;
	border-radius: 8px;
	background-color: #fff;
	color: var(--color-navy-blue, #2c3e50);
	cursor: pointer;
	transition: all 0.3s ease;
	outline: none;
}

.dashboard-customizado__select:hover {
	border-color: var(--color-green, #4caf50);
}

.dashboard-customizado__select:focus {
	border-color: var(--color-green, #4caf50);
	box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.dashboard-customizado__icone {
	padding: 0.4rem 0.75rem;
	font-size: 0.875rem;
	color: var(--color-navy-blue, #2c3e50);
	background-color: transparent;
	border: 2px solid #e0e0e0;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.dashboard-customizado__icone:hover:not(:disabled) {
	border-color: var(--color-green, #4caf50);
	color: var(--color-green, #4caf50);
}

.dashboard-customizado__icone:disabled {
	cursor: not-allowed;
	opacity: 0.5;
}

.dashboard-customizado__conteudo {
	flex: 1;
	min-height: 0;
	position: relative;
}

/*
	Alinhado a direita do mesmo container de 1200px do menu (Menu.vue).
	pointer-events: none para nao capturar cliques do relatorio embaixo.
*/
.dashboard-customizado__reabrir-area {
	position: absolute;
	top: 0.75rem;
	left: 50%;
	transform: translateX(-50%);
	z-index: 1;
	display: flex;
	justify-content: flex-end;
	width: 100%;
	max-width: 1200px;
	padding: 0 2rem;
	pointer-events: none;
}

@media screen and (min-width: 1200px) {
	.dashboard-customizado__reabrir-area {
		padding: 0;
	}
}

.dashboard-customizado__reabrir {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	max-width: 18rem;
	padding: 0.4rem 0.75rem;
	font-size: 0.8rem;
	font-weight: 600;
	color: var(--color-navy-blue, #2c3e50);
	background-color: rgba(255, 255, 255, 0.95);
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
	cursor: pointer;
	/* Escondido enquanto o mouse nao entra na area do relatorio */
	opacity: 0;
	pointer-events: none;
	transition:
		opacity 0.2s ease,
		border-color 0.2s ease,
		color 0.2s ease;
}

.dashboard-customizado__conteudo:hover .dashboard-customizado__reabrir {
	opacity: 0.85;
	pointer-events: auto;
}

.dashboard-customizado__reabrir:hover {
	opacity: 1;
	border-color: var(--color-green, #4caf50);
	color: var(--color-green, #4caf50);
}

/* Em telas sem hover (touch) o botao precisa ficar sempre visivel */
@media (hover: none) {
	.dashboard-customizado__reabrir {
		opacity: 0.85;
		pointer-events: auto;
	}
}

.dashboard-customizado__reabrir-texto {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.dashboard-customizado__frame {
	width: 100%;
	height: 100%;
	border: 0;
}

.dashboard-customizado__estado {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 1rem;
	height: 100%;
	color: var(--color-gray, #666);
	text-align: center;
	padding: 0 1rem;
}

.dashboard-customizado__estado-icone {
	font-size: 2rem;
	opacity: 0.4;
}

.dashboard-customizado__tentar-novamente {
	padding: 0.5rem 1.25rem;
	font-size: 0.9rem;
	font-weight: 600;
	color: #fff;
	background-color: var(--color-green, #4caf50);
	border: none;
	border-radius: 8px;
	cursor: pointer;
}

@media screen and (max-width: 768px) {
	.dashboard-customizado__barra-conteudo {
		align-items: stretch;
		flex-direction: column;
		padding: 0.75rem 1rem;
	}

	.dashboard-customizado__select {
		max-width: 100%;
	}

	.dashboard-customizado__reabrir-texto {
		display: none;
	}
}
</style>
